import * as Yup from 'yup'
import { IoTOdometerStatus } from './CreateTemplateModal.interfaces'
import { IApiResult } from 'auth/interface/api.interface'

interface ValidationSchema {
    [vehicleId: number]: IoTOdometerStatus
}

export const getValidationSchemas = (checkTemplateListResult: IApiResult, t: any, vehicleIoTOdometerData?: ValidationSchema) => [
    // Step 1: Basic settings
  Yup.object({
    name: Yup.string()
      .required(t("global.name_required", "Name is required"))
      .min(3, t("global.name_minimum_char", "The name must be at least {count} characters").replace("{count}", "3"))
      .test('unique-name', t("checks.validation.template_name_taken", "A check template with this name already exists"), function(value) {
        if (checkTemplateListResult.status !== 2) return true
        const { id } = this.parent
        const existingTemplates = checkTemplateListResult.data || []
        if (!value) return true

        return !existingTemplates.some((template: any) =>
          template.name.toLowerCase() === value.toLowerCase() && template.id !== id
        )
      }),

    usesDate: Yup.boolean().test('at-least-one-type', t("checks.choose_controltype", "You must select at least one check type"), function(value) {
      const { usesDistance, usesInspection } = this.parent
      return value || usesDistance || usesInspection
    }),
    usesDistance: Yup.boolean(),
    usesInspection: Yup.boolean(),
    dateInterval: Yup.number().when('usesDate', {
      is: true,
      then: (schema) => schema.required(t("checks.validation.date_interval_required", "Date interval is required")).min(1, t("global.validation.minimum_count", "Must be at least {count}")?.replace("{count}", "1")),
    }),
    distanceInterval: Yup.number().when('usesDistance', {
      is: true,
      then: (schema) => schema.required('Avståndsintervall är obligatoriskt').min(1, t("global.validation.minimum_count", "Must be at least {count}")?.replace("{count}", "1")),
    }),
  }).test('at-least-one', t("checks.choose_controltype", "You must select at least one check type"), function (value) {
      return value.usesDate || value.usesDistance || value.usesInspection
  }),

  // Step 2: Select vehicles
  Yup.object({
    selectedVehicles: Yup.array()
      .min(1, t("global.validation.select_count_vehicle", "Pick at least {count} vehicles").replace("{count}", "1")),
  }),

  // Step 3: Vehicle values - Dynamic validation based on usesDate and usesDistance
  Yup.object({
    usesDate: Yup.boolean(),
    usesDistance: Yup.boolean(),
    usesInspection: Yup.boolean(),
    selectedVehicles: Yup.array(),
    vehicleLastValues: Yup.lazy((values, options) => {
      const { usesDate, usesDistance, usesInspection, selectedVehicles } = options.parent

      // If only inspection is used, no validation needed
      if (usesInspection && !usesDate && !usesDistance) {
          return Yup.object()
      }

      // Build dynamic shape for each selected vehicle
      const shape: any = {}

      if (selectedVehicles && Array.isArray(selectedVehicles)) {
        selectedVehicles.forEach((vehicleId: number) => {
          const vehicleShape: any = {}

          if (usesDate) {
            vehicleShape.lastDate = Yup.date()
              .nullable()
              .required(t("checks.validation.last_service_required", "Last service date is mandatory"))
              .typeError(t("global.validation.input_valid_date", "You must enter a valid date"))
          }

          if (usesDistance) {
            let distanceSchema = Yup.number()
              .required(t("checks.validation.lastest_metersetting_required", "Latest meter reading is required"))
              .typeError(t("global.validation.enter_valid_number", "You must enter a valid number"))
              .min(0, t("global.validation.positive_metersetting", "Meter reading cannot be negative"))
              .integer(t("global.validation.integer_metersetting", "Meter reading must be an integer"))

            // Add validation against current odometer if data is available
            if (vehicleIoTOdometerData && vehicleIoTOdometerData[vehicleId]?.currentOdometer) {
              const currentOdometer = vehicleIoTOdometerData[vehicleId].currentOdometer!
              distanceSchema = distanceSchema.max(
                Math.round(currentOdometer),
                t("global.validation.metersetting_higher_than", "Meter reading cannot be higher than current: {{amount}} km", { amount: Math.round(currentOdometer).toLocaleString('sv-SE') })
              )
            }

            vehicleShape.lastDistance = distanceSchema
          }

          shape[vehicleId] = Yup.object(vehicleShape)
        })
      }

      return Yup.object(shape)
    }),
  }),

  // Step 4: Summary (no validation needed)
  Yup.object({}),
]

// export const validationSchemas = getValidationSchemas()
